import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useMemo } from 'react';
import { t } from 'ttag';
import type { ImageECGProps } from './imageECG.types';

/**
 * Drives the ECG strip image gallery of an event row.
 * Tracks image loading and load errors, maps the strip URLs to gallery items with position
 * captions and alt text, and calls forceRerender once loading ends.
 *
 * @param props - Gallery props carrying stripImgs and forceRerender.
 * @returns The loading state, the gallery items and the image load and error handlers.
 */
export const useImageECG = ({
  stripImgs,
  forceRerender,
}: Pick<ImageECGProps, 'stripImgs' | 'forceRerender'>) => {
  const [state, setState] = useMergeState({
    isLoading: true,
    isLoadedImageError: false,
  });

  /**
   * Clears the loading state once an ECG strip image has rendered. Acts only while loading is still in progress, setting isLoading to false.
   *
   * @returns Nothing.
   */
  const onImageLoad = () => {
    if (state.isLoading) {
      setState({ isLoading: false });
    }
  };

  /**
   * Ends the loading state and flags a failure when an ECG strip image cannot be loaded. Acts only while loading is still in progress, clearing isLoading and setting isLoadedImageError.
   *
   * @returns Nothing.
   */
  const onImageError = () => {
    if (state.isLoading) {
      setState({
        isLoading: false,
        isLoadedImageError: true,
      });
    }
  };

  /**
   * Builds the gallery item list for the event's ECG strip images. Memoized on the strip URL list, mapping each URL to an item that uses it as both original and thumbnail and a description of the form position over total.
   *
   * @returns Gallery items for the strip images.
   */
  const createItems = useMemo(
    () =>
      _.map(stripImgs, (stripImg, index) => ({
        original: stripImg,
        thumbnail: stripImg,
        description: `${index + 1}/${stripImgs.length}`,
        originalAlt: `${t`ECG strip`} ${index + 1}/${stripImgs.length}`,
        thumbnailAlt: `${t`ECG strip`} ${index + 1}/${stripImgs.length}`,
      })),
    [stripImgs],
  );

  useUpdateEffect(() => {
    if (!state.isLoading) {
      forceRerender?.();
    }
  }, [state.isLoading]);

  return {
    state,
    createItems,
    onImageLoad,
    onImageError,
  };
};
